import CircularProgress from '@material-ui/core/CircularProgress';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { bindActionCreators } from 'redux';

import {
  checkDomainReactivation,
  closeChangeOrder,
  fetchActionHistory,
  fetchCustomerOrders,
  fetchEmailAccountsToRecover,
  fetchNoOfAccountsExceedingQuota,
  getAllSuspensions,
  getCustomerSuspensions,
  getEntitySuspensionDetails,
  initiateChangeOrder,
  navigateBackBundleList,
  navigateBackOrderList,
} from '../../actions/orderActions';
import { ReactComponent as CloseGrey } from '../../assets/close-grey.svg';
import { ReactComponent as NavigateBack } from '../../assets/navigate_back.svg';
import { ReactComponent as Red } from '../../assets/oval_red.svg';
import {
  MAIL_SERVICE_TYPES,
  ORDER_TYPES,
  BUNDLE_STATUSES
} from '../../helpers/constants';
import {
  getLatestDomainOrder,
  getLatestMailOrder,
  getLatestSiteOrder,
  hasDomainOrder,
  hasMailOrder,
  hasSiteOrder,
} from '../../helpers/product.helpers';
import { CustomerSuspensionSource } from '../../model/customerSuspensionDetails';
import { getAdminPanelLoginUrlRequest } from '../../service/orderService';
import { ORDER_DETAIL_ACTION } from '../../utils/const';
import {
  getFirstTrialPlan,
  isFreePlan as _isFreePlan,
  isNeoPartner,
  isMyPartner,
  toCapitalize,
} from '../../utils/helper';
import medusa from '../../utils/medusa';
import AlertModal from '../AlertModal';
import BundleDetailsEmailInfo from '../BundleDetailsEmailInfo/BundleDetailsEmailInfo';
import ChangeCustomerEmailModal from '../ChangeCustomerEmailModal';
import ChangeMailService from '../ChangeMailService/ChangeMailService';
import ChangePlanOrder from '../ChangePlan/changePlanOrder';
import CustomButton from '../Common/Button/Button';
import DeleteOrder from '../deleteOrder';
import DnsDetails from '../dnsDetails';
import DomainFeatureSettings from '../DomainFeatureSettings/DomainFeatureSettings';
import GenerateInvoice from '../generateInvoice';
import ManageButton from '../manageButton';
import ModifyAccountLimitModal from '../modifyAccountLimitModal';
import ProcessRefund from '../ProcessRefund/ProcessRefund';
import ReactivateDomainModal from '../reactivateDomainModal';
import SuspendCustomerConfirmation from '../suspendCustomerConfirmation';
import Suspension from '../suspension';
import SuspensionPopover from '../suspensionPopover';
import Unsuspension from '../unsuspension';
import {
  capitalize,
  DEFAULT_PAGINATION_LIMIT,
  ENTITY_TYPE,
  getDomainType,
  isInternalDomain,
  isOrderStatusActive,
  isOrderStatusDeleted,
  isOrderStatusSuspended,
  isRoleTitan,
  showEmailSection,
} from '../utils';
import OrderDetailsItem from './orderDetailsItem';
import OrderDetailsTooltipItem from './orderDetailsTooltipItem';
import OrderStatusIndicator from '../orderStatusIndicator';
import { ORDER_SERVICEABLE_STATUS_SUPPORT_URL } from '../../constants/urls';

const OrderDetails = (props) => {
  // Local state
  const [changePlanAction, setChangePlanAction] = useState('');
  const [buttonClicked, setButtonClicked] = useState('');
  const [orderType, setOrderType] = useState('');
  const [isAdminPanelUrlFetching, setIsAdminPanelUrlFetching] = useState(false);
  const [selectedEmailAccountId, setSelectedEmailAccountId] = useState(null);
  const [refreshEmailInfo, setRefreshEmailInfo] = useState(null);

  const topDivRef = useRef(null);

  useEffect(() => {
    if (topDivRef.current) {
      topDivRef.current.scrollIntoView();
    }
    fetchOrderSuspensions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orderDetails]);

  // --- Helper functions ---
  const handleAdminLoginPanelClick = async (bundleId) => {
    setIsAdminPanelUrlFetching(true);

    try {
      const res = await getAdminPanelLoginUrlRequest(bundleId);
      if (res && res.ssoUrl) {
        window.open(res.ssoUrl, '_blank');
      }
    } catch (err) {
      console.error('Error fetching admin panel URL:', err);
      toast.error(`Error fetching admin panel URL. Error: ${err}`);
    } finally {
      setIsAdminPanelUrlFetching(false);
    }
  };

  const handleDomainHistoryClicked = (bundleId) => {
    props.fetchActionHistory(
      bundleId,
      null,
      ENTITY_TYPE.ORDER,
      props.token,
      DEFAULT_PAGINATION_LIMIT
    );
  };

  const handleChangePlanOrderClick = (action, order) => {
    if (action === ORDER_DETAIL_ACTION.EXTEND_PLAN && !order.expiryDate) {
      toast.info(
        'Unable to extend plan for this order as it does not have an expiry date'
      );
      return;
    }

    handleManageButtonClick('changePlanOrder');
    setChangePlanAction(action);
    setOrderType(order.type);

    if (
      action === ORDER_DETAIL_ACTION.CHANGE_PLAN &&
      order.type === ORDER_TYPES.MAIL
    ) {
      props.fetchNoOfAccountsExceedingQuota(
        order.id,
        props.token,
        getCurrentRegion()
      );
    }
  };

  const handleDeleteOrderClicked = (order) => {
    setOrderType(order.type);
    handleManageButtonClick('deleteOrder');
  };

  const handleToggleMailServiceClick = (item) => {
    handleManageButtonClick(item);
  };

  const handleUnsuspendButtonClick = (item) => {
    handleManageButtonClick(item);
    // Original unsuspend logic for orders/accounts can be added here if needed.
  };

  const handleSuspendOrderClick = (order) => {
    setOrderType(order.type);
    handleManageButtonClick('suspendOrder');
  };

  const handleUnsuspendOrderClick = (order) => {
    setOrderType(order.type);
    handleUnsuspendButtonClick('unsuspendOrder');
  };

  const getCustomerEmail = () => {
    const { bundle } = props.orderDetails;
    return bundle.customer.email;
  };

  const getCustomerId = () => {
    const { bundle } = props.orderDetails;
    return bundle.customer.id;
  };

  const getCurrentRegion = () => props.currentOrderSelected.region;

  const handleSuspendCustomerClick = (item) => {
    handleManageButtonClick(item);
    props.fetchCustomerOrders(getCustomerId(), getCustomerEmail());
  };

  const handleUnsuspendCustomerClick = (item) => {
    handleManageButtonClick(item);
    // Additional unsuspend customer logic (if any) can be added here.
  };

  const handleRecoverDataButtonClick = (order) => {
    if (!order) {
      toast.error('No order / order ID found to recover data');
      return;
    }
    props.fetchEmailAccountsToRecover(
      props.orderDetails.bundle.domainName,
      order.id
    );
  };

  const handleChangeCustomerEmailButtonClick = () => {
    handleManageButtonClick('changeCustomerEmail');
  };

  const handleSetAccountLimitButtonClick = () => {
    handleManageButtonClick('modifyAccountLimit');
  };

  const handleManageButtonClick = async (buttonName) => {
    if (buttonName === 'markDomainSafe') {
      const {
        partner: { id },
      } = bundle;
      await medusa.markDomainSafe(id);
      return toast.info('Domain has been marked as safe');
    } else if (buttonName === 'markCustomerSafe') {
      await medusa.markCustomerSafe();
      return toast.info('Customer has been marked as safe');
    }
    props.initiateChangeOrder();
    setButtonClicked(buttonName);
  };

  const handleCloseChangeOrderClick = () => {
    const { bundle } = props.orderDetails;
    props.closeChangeOrder(bundle.id, props.token);
  };

  const handleCloseToggleMailServiceClick = (bundleId) => {
    const { bundle } = props.orderDetails;
    if (bundleId && bundleId !== bundle.id) {
      props.closeChangeOrder(bundleId, props.token);
    } else {
      props.closeChangeOrder(bundle.id, props.token);
    }
  };

  const fetchOrderSuspensions = () => {
    if (props.orderDetails.orders) {
      const orders = props.orderDetails.orders;
      const entityIds = [];
      if (hasMailOrder(orders)) {
        entityIds.push(getLatestMailOrder(orders).id);
      }
      if (hasSiteOrder(orders)) {
        entityIds.push(getLatestSiteOrder(orders).id);
      }
      if (hasDomainOrder(orders)) {
        entityIds.push(getLatestDomainOrder(orders).id);
      }
      if (entityIds.length > 0) {
        props.getEntitySuspensionDetails(
          entityIds,
          ENTITY_TYPE.ORDER.toUpperCase(),
          props.token
        );
      }
    }
  };

  const handleCloseSuspensionUnsuspensionModal = (
    entityType,
    shouldRefreshData = true
  ) => {
    handleCloseChangeOrderClick();
    if (shouldRefreshData) {
      if (entityType === ENTITY_TYPE.ACCOUNT) {
        setRefreshEmailInfo(Date.now());
      } else {
        const entityIds = [];
        if (entityType === ENTITY_TYPE.ORDER) {
          const mailId = getLatestMailOrder(props.orderDetails.orders)?.id;
          const siteId = getLatestSiteOrder(props.orderDetails.orders)?.id;
          mailId && entityIds.push(mailId);
          siteId && entityIds.push(siteId);
        } else if (entityType === ENTITY_TYPE.CUSTOMER) {
          entityIds.push(props.orderDetails.bundle?.customer.id);
        }
        props.getEntitySuspensionDetails(
          entityIds,
          entityType.toUpperCase(),
          props.token
        );
      }
    }
  };

  const retryGetAllSuspensions = () => {
    const suspensionType =
      buttonClicked === 'unsuspendDomain'
        ? ENTITY_TYPE.ORDER
        : ENTITY_TYPE.ACCOUNT;
    const accountId = getAccountId();
    props.getAllSuspensions(
      props.orderDetails.domain.id,
      accountId,
      props.token,
      suspensionType,
      getCurrentRegion()
    );
  };

  const closeAlertModal = () => {
    handleCloseChangeOrderClick();
  };

  const refreshOrderDetailsOnModalClose = () => handleCloseChangeOrderClick();

  const isBundleStatusDeleted = (status) => status.toLowerCase() === BUNDLE_STATUSES.DELETED;

  const openCustomerSuspensionModal = () => {
    setButtonClicked('suspendCustomerConfirmation');
  };

  const getCustomerDetails = (customerName, customerEmail, customerId) => {
    if (!customerName && !customerEmail) return null;

    if (customerName) {
      return (
        <div className="order-details-customer-container">
          <span className="order-details-customer-name-text">
            {getCustomerNameWithTooltip(customerName)}
          </span>
          <div className="order-details-customer-name-separator" />
          <div>{getEmailWithTooltip(customerEmail)}</div>
          <div className="order-details-customer-name-separator" />
          <div>{customerId}</div>
        </div>
      );
    } else {
      return (
        <div className="order-details-customer-email-text">
          {getEmailWithTooltip(customerEmail)}
        </div>
      );
    }
  };

  const getCustomerEmailStatus = (customerName, customerEmail, customerId) => {
    if (props.isCustomerSuspended) {
      return (
        <div className="flex">
          <div className="customer-email-suspension-container">
            <Red className="state-logo" />
            <b className="customer-email-suspension-status-text">Suspended</b>
            <div className="customer-email-suspension-subheading">
              {getCustomerDetails(customerName, customerEmail, customerId)}
            </div>
          </div>
          <SuspensionPopover
            entityType={ENTITY_TYPE.CUSTOMER}
            accountId={props.orderDetails.bundle.customer.id}
            hoverIconCss="order-status-popup-icon order-status-popup-icon-customer-email"
            customerEmail={customerEmail}
          />
        </div>
      );
    } else {
      return getCustomerDetails(customerName, customerEmail, customerId);
    }
  };

  const getEmailWithTooltip = (email) => {
    if (email.length > 30) {
      return (
        <div className="tooltip">
          {email.substr(0, 8) + '...' + email.substr(email.length - 10)}
          <span className="tooltiptext tooltiptextCustomer">{email}</span>
        </div>
      );
    }
    return email;
  };

  const getCustomerNameWithTooltip = (name) => {
    if (name.length > 20) {
      return (
        <div className="tooltip">
          {name.substr(0, 10) + '...'}
          <span className="tooltiptext tooltiptextCustomer">{name}</span>
        </div>
      );
    }
    return name;
  };

  const getFormattedDate = (date) => {
    const momentDate =
      typeof date === 'number'
        ? moment.unix(date / 1000)
        : moment(date, 'YYYY-MM-DD');
    return momentDate.format('Do MMM YYYY');
  };

  const handleSuspendUnsuspendEmailAccount = (accountId, buttonName) => {
    setSelectedEmailAccountId(accountId);
    handleManageButtonClick(buttonName);
  };

  const getAccountId = () => {
    const account = props.orderDetails.account;
    return account ? account.id : null;
  };

  const handleReactivateDomainClicked = (order) => {
    if (_isNeoOrMyPartner) {
      // show toast
      toast.info(
        'Reactivation is not currently supported for Neo partner orders'
      );
      return;
    }

    props.initiateChangeOrder();
    setButtonClicked('reactivateDomain');
    props.checkDomainReactivation(order.id, props.token, getCurrentRegion());
  };

  const handleProcessRefundClick = (item) => {
    handleManageButtonClick(item);
  };

  const getReactivateButton = () => {
    // Non-Neo partners only have mail orders
    let buttonText = <FormattedMessage id="reactivate" />;
    const latestMailOrder = getLatestMailOrder(orders);
    if (props.domainReactivationCheckInfo.isDomainReactivationCheckInProgress) {
      buttonText = (
        <div className="manage-button-div">
          <div className="spinner-manage reactive-check" />
          <FormattedMessage id="checking" />
        </div>
      );
    }
    return (
      <button
        className="button button-secondary order-details-button"
        onClick={() => handleReactivateDomainClicked(latestMailOrder)}
      >
        {buttonText}
      </button>
    );
  };

  const renderStatusIndicator = (status, type, options = {}) => {
    if (isOrderStatusActive(status)) {
      return (
        <div>
          <OrderStatusIndicator className='state-logo' status={status} />
        </div>
      );
    } else {
      const isStatusSuspended = isOrderStatusSuspended(status);
      return (
        <div className="order-status-container">
          <div>
            <OrderStatusIndicator className='state-logo' status={status} />
          </div>
          {isStatusSuspended && type ? (
            <SuspensionPopover
              accountId={options?.accountId}
              entityType={type}
              orderType={options?.orderType}
              hoverIconCss="order-status-popup-icon"
            />
          ) : null}
        </div>
      );
    }
  };

  // --- Render logic ---
  const { orderDetails } = props;
  const {
    bundle,
    orders,
    plans: availablePlans,
    shouldShowToggleButton,
    reseller,
  } = orderDetails;
  const roleIsTitan = isRoleTitan(props.userRole);
  const domainId = bundle.id;
  const allMailSuiteOrders = orders
    .filter((order) => order.type === 'MailSuite')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const allSiteOrders = orders
    .filter((order) => order.type === 'Site')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const allDomainOrders = orders
    .filter((order) => order.type === 'NeoDomain')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const activeMailSuiteOrder = allMailSuiteOrders.find(
    (order) => isOrderStatusActive(order.status)
  );
  // domainOrder not used further in this code sample
  const billingManagedBy = capitalize(bundle.paymentManagedBy);
  const domainName = bundle.domainName;
  let bundleStatus = capitalize(bundle.status);
  const domainCreationDate = bundle.createdAt;
  const showLoginToAdminPanelButton =
    !isBundleStatusDeleted(bundleStatus) &&
    !isInternalDomain(domainName);
  const getPlanForOrder = (order) => {
    const plan = availablePlans.find(
      (plan) => plan.planType === order.plan.planType
    );
    return {
      ...plan,
      isFreePlan: _isFreePlan(plan),
    };
  };
  const accessToMultiplePartners =
    props.userPartners && props.userPartners.length > 1;
  const _isNeoPartner = isNeoPartner(bundle.partner.id);
  const _isMyPartner = isMyPartner(bundle.partner.id);
  const _isNeoOrMyPartner = _isNeoPartner || _isMyPartner;
  const getPartnerInfo = () => {
    let region = props.region;
    let regionInfo =
      region && region !== 'us' ? ` (${region.toUpperCase()})` : '';
    return bundle.partner.name + regionInfo;
  };

  let infoRows = [];
  const commonRows = [
    {
      itemName: 'order-details-partner-info',
      itemValue: getPartnerInfo(),
      isVisible: roleIsTitan || accessToMultiplePartners,
    },
    {
      itemName: 'order-details-customer-account',
      itemValue: getCustomerEmailStatus(
        bundle.customer.name,
        bundle.customer.email,
        bundle.customer.id
      ),
      isVisible: !!bundle.customer.email,
    },
    {
      itemName: 'partnerId',
      itemValue: bundle.partner.id,
      isVisible: true,
    },
    {
      itemName: 'order-details-reseller-id',
      itemValue: bundle.resellerId,
      isVisible: !!bundle.resellerId,
      valueTooltipText: reseller?.resellerName,
    },
    {
      itemName: 'order-details-domain',
      itemValue: <OrderDetailsTooltipItem value={domainName} />,
      isVisible: true,
    },
    {
      itemName: 'order-details-bundle-id',
      itemValue: bundle.id,
      isVisible: _isNeoOrMyPartner,
    },
    {
      itemName: 'order-details-bundle-status',
      itemValue: renderStatusIndicator(bundle.status),
      isVisible: _isNeoOrMyPartner,
    },
    {
      itemName: 'order-details-bundle-creation-date',
      itemValue: getFormattedDate(bundle.createdAt),
      isVisible: _isNeoOrMyPartner,
    },
    {
      itemName: 'order-details-bundle-expiry-date',
      itemValue: bundle.expiry ? getFormattedDate(bundle.expiry) : '-',
      isVisible: _isNeoOrMyPartner,
    },
  ];

  const getMailOrderRows = (order) => {
    const maxNoOfAccs = order.mailOrderDetails?.quota?.accountsLimit || 0;
    const activeAccounts =
      order.mailOrderDetails?.accountCountPerStatus?.active || 0;
    const suspendedAccounts =
      order.mailOrderDetails?.accountCountPerStatus?.suspended || 0;
    const noOfAccsCreated = activeAccounts + suspendedAccounts || 0;
    const plan = availablePlans.find((p) => p.planType === order.plan.planType);
    const planName = plan.name;
    return [
      {
        itemName: _isNeoOrMyPartner
          ? 'order-details-orderId'
          : 'order-details-domainId',
        itemValue: <OrderDetailsTooltipItem value={order.id} />,
        isVisible: true,
      },
      {
        itemName: 'mail-order-details-status',
        itemValue: renderStatusIndicator(order.status, ENTITY_TYPE.ORDER, {
          orderType: ORDER_TYPES.MAIL,
        }),
        tooltipText: `Determines if the email service is serviceable on the domain<br/><a href='${ORDER_SERVICEABLE_STATUS_SUPPORT_URL}' target='_blank'>Learn more</a>`,
        isVisible: true,
      },
      {
        itemName: 'order-details-email-accounts',
        itemValue: (
          <OrderDetailsTooltipItem
            value={noOfAccsCreated + ' / ' + maxNoOfAccs}
          />
        ),
        isVisible: true,
      },
      {
        itemName: 'order-details-plan-details',
        itemValue: (
          <OrderDetailsTooltipItem
            value={
              <>
                {planName.toUpperCase()} | {plan.id} | {plan.planType}
              </>
            }
          />
        ),
        isVisible: _isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-plan-name',
        itemValue: <OrderDetailsTooltipItem value={planName.toUpperCase()} />,
        isVisible: !_isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-plan-id',
        itemValue: plan.id,
        isVisible: !_isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-plan-type',
        itemValue: plan.planType,
        isVisible: !_isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-creation-date',
        itemValue: (
          <OrderDetailsTooltipItem value={getFormattedDate(order.createdAt)} />
        ),
        isVisible: true,
      },
      {
        itemName: 'order-details-expiry-date',
        itemValue: (
          <OrderDetailsTooltipItem
            value={order.expiryDate ? getFormattedDate(order.expiryDate) : '-'}
          />
        ),
        isVisible: true,
      },
    ];
  };

  const getSiteOrderRows = (order) => {
    const sitePlanDetails = getPlanForOrder(order);
    return [
      {
        itemName: _isNeoOrMyPartner
          ? 'order-details-orderId'
          : 'order-details-domainId',
        itemValue: <OrderDetailsTooltipItem value={order?.id} />,
        isVisible: true,
      },
      {
        itemName: 'order-details-status',
        itemValue: renderStatusIndicator(order?.status, ENTITY_TYPE.ORDER, {
          orderType: ORDER_TYPES.SITE,
        }),
        isVisible: true,
      },
      {
        itemName: 'order-details-plan-details',
        itemValue: (
          <OrderDetailsTooltipItem
            value={
              <>
                {sitePlanDetails.name.toUpperCase()} | {sitePlanDetails.id} |{' '}
                {sitePlanDetails.planType}
              </>
            }
          />
        ),
        isVisible: _isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-plan-name',
        itemValue: (
          <OrderDetailsTooltipItem value={sitePlanDetails.name.toUpperCase()} />
        ),
        isVisible: !_isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-plan-id',
        itemValue: sitePlanDetails.id,
        isVisible: !_isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-plan-type',
        itemValue: sitePlanDetails.planType,
        isVisible: !_isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-creation-date',
        itemValue: (
          <OrderDetailsTooltipItem value={getFormattedDate(order?.createdAt)} />
        ),
        isVisible: true,
      },
      {
        itemName: 'order-details-expiry-date',
        itemValue: (
          <OrderDetailsTooltipItem
            value={order?.expiryDate ? getFormattedDate(order.expiryDate) : '-'}
          />
        ),
        isVisible: true,
      },
    ];
  };

  const getDomainOrderRows = (order) => {
    const domainPlanDetails = getPlanForOrder(order);
    return [
      {
        itemName: _isNeoOrMyPartner
          ? 'order-details-orderId'
          : 'order-details-domainId',
        itemValue: <OrderDetailsTooltipItem value={order?.id} />,
        isVisible: true,
      },
      {
        itemName: 'order-details-status',
        itemValue: renderStatusIndicator(order?.status, ENTITY_TYPE.ORDER, {
          orderType: ORDER_TYPES.SITE,
        }),
        isVisible: true,
      },
      {
        itemName: 'order-details-plan-details',
        itemValue: (
          <OrderDetailsTooltipItem
            value={
              <>
                {domainPlanDetails.name.toUpperCase()} | {domainPlanDetails.id}{' '}
                | {domainPlanDetails.planType}
              </>
            }
          />
        ),
        isVisible: _isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-plan-name',
        itemValue: (
          <OrderDetailsTooltipItem
            value={domainPlanDetails.name.toUpperCase()}
          />
        ),
        isVisible: !_isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-plan-id',
        itemValue: domainPlanDetails.id,
        isVisible: !_isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-plan-type',
        itemValue: domainPlanDetails.planType,
        isVisible: !_isNeoOrMyPartner,
      },
      {
        itemName: 'order-details-creation-date',
        itemValue: (
          <OrderDetailsTooltipItem value={getFormattedDate(order?.createdAt)} />
        ),
        isVisible: true,
      },
      {
        itemName: 'order-details-expiry-date',
        itemValue: (
          <OrderDetailsTooltipItem
            value={order?.expiryDate ? getFormattedDate(order.expiryDate) : '-'}
          />
        ),
        isVisible: true,
      },
    ];
  };

  const otherDetailsRows = [
    {
      itemName: 'order-details-current-billing-cycle',
      itemValue: (
        <OrderDetailsTooltipItem
          value={
            bundle.billingCycle
              ? capitalize(bundle.billingCycle.replaceAll('_', ' '))
              : 'N/A'
          }
        />
      ),
      isVisible: _isNeoOrMyPartner,
    },
    {
      itemName: 'order-details-renewal-billing-cycle',
      itemValue: (
        <OrderDetailsTooltipItem
          value={
            bundle.renewalBillingCycle
              ? capitalize(bundle.renewalBillingCycle.replaceAll('_', ' '))
              : 'N/A'
          }
        />
      ),
      isVisible: _isNeoOrMyPartner,
    },
    {
      itemName: 'order-details-source',
      itemValue: (
        <OrderDetailsTooltipItem
          value={`${bundle.source || ''} : ${
            getDomainType(
              `${allMailSuiteOrders[0]?.mailOrderDetails.domainType}`
            ) || '-'
          } : ${bundle.customer.id}`}
        />
      ),
      isVisible: true,
    },
    {
      itemName: 'order-details-billing-managed-by',
      itemValue: <OrderDetailsTooltipItem value={billingManagedBy} />,
      isVisible: roleIsTitan,
    },
  ];

  if (_isNeoOrMyPartner) {
    infoRows.push(...commonRows);
    if (allMailSuiteOrders.length > 0) {
      allMailSuiteOrders.forEach((mailSuiteOrder) => {
        infoRows.push(
          { heading: 'Mail Order' },
          ...getMailOrderRows(mailSuiteOrder)
        );
      });
    }
    if (allSiteOrders.length > 0) {
      allSiteOrders.forEach((siteOrder) => {
        infoRows.push(
          { heading: 'Site Order' },
          ...getSiteOrderRows(siteOrder)
        );
      });
    }
    if (allDomainOrders.length > 0) {
      allDomainOrders.forEach((domainOrder) => {
        infoRows.push(
          { heading: 'Neo Domain Order' },
          ...getDomainOrderRows(domainOrder)
        );
      });
    }
    infoRows.push({ heading: 'Other Details' }, ...otherDetailsRows);
  } else {
    infoRows = [
      ...commonRows,
      ...getMailOrderRows(allMailSuiteOrders[0]),
      ...otherDetailsRows,
    ];
  }

  const sections = infoRows.reduce((acc, row) => {
    if ('heading' in row) {
      // start a new section
      acc.push({ heading: row.heading, rows: [] });
    } else {
      // append to the latest section
      const current = acc[acc.length - 1];
      if (current) {
        current.rows.push(row);
      } else {
        // edge-case: a row before any heading—treat it as its own section
        acc.push({ heading: '', rows: [row] });
      }
    }
    return acc;
  }, []);

  // const finalDomainObj = infoRows.map((row, index) => {
  //   if (row.heading) {
  //     return (
  //       <div key={`heading-${index}`} className="order-details-heading">
  //         <span>{row.heading}</span>
  //       </div>
  //     );
  //   } else {
  //     return row.isVisible ? (
  //       <div className="order-details-info-row" key={index}>
  //         <OrderDetailsItem itemName={row.itemName} itemValue={row.itemValue} />
  //       </div>
  //     ) : null;
  //   }
  // });

  const finalDomainObj = sections.map((section, secIndex) => (
    <>
      <div
        key={`section-${secIndex}`}
        className="order-details-section" // your wrapper
      >
        {section.heading && (
          <div className="order-details-heading">
            <span>{section.heading}</span>
          </div>
        )}

        {section.rows.map((row, rowIndex) =>
          row.isVisible ? (
            <div
              className="order-details-info-row"
              key={`row-${secIndex}-${rowIndex}`}
            >
              <OrderDetailsItem
                itemName={row.itemName}
                itemValue={row.itemValue}
                tooltipText={row.tooltipText}
                valueTooltipText={row.valueTooltipText}
              />
            </div>
          ) : null
        )}
      </div>
    </>
  ));

  const mailSectionVisible = showEmailSection(props.queryText);
  const catchall = activeMailSuiteOrder?.mailOrderDetails?.catchall;
  const email = getCustomerEmail();
  const mailOrderIds = allMailSuiteOrders.map((order) => order.id);

  // Modal content handling
  let modalContent = '';
  let suspensionType;
  let suspensionListError;
  let fetchSuspensionListSuccess;
  let suspensionList = [];

  switch (buttonClicked) {
    case 'deleteOrder':
      modalContent = (
        <DeleteOrder
          orderType={orderType}
          closeModal={handleCloseChangeOrderClick}
        />
      );
      break;
    case 'changePlanOrder': {
      const {
        partner: { id, name },
      } = bundle;
      modalContent = (
        <ChangePlanOrder
          action={changePlanAction}
          currentRegion={getCurrentRegion()}
          domainCreationDate={domainCreationDate}
          domainId={domainId}
          domainName={domainName}
          orderType={orderType}
          partnerId={id}
          partnerName={name}
          closeModal={handleCloseChangeOrderClick}
        />
      );
      break;
    }
    case 'toggleMailService':
      const mailOrder = getLatestMailOrder(props.orderDetails.orders);
      if (!mailOrder) {
        toast.error('No mail order found to toggle mail service');
        return;
      }
      modalContent = (
        <ChangeMailService
          userId={getCustomerId()}
          mailService={
            mailOrder.mailOrderDetails?.isFlockCloud
              ? MAIL_SERVICE_TYPES.FLOCK_CLOUD
              : MAIL_SERVICE_TYPES.PARTNER_HOSTING
          }
          domainName={domainName}
          accountId={null}
          currentRegion={getCurrentRegion()}
          closeModal={handleCloseToggleMailServiceClick}
        />
      );
      break;
    case 'suspendOrder':
      modalContent = (
        <Suspension
          suspensionType={ENTITY_TYPE.ORDER}
          orderType={orderType}
          closeModal={handleCloseChangeOrderClick}
        />
      );
      break;
    case 'suspendAccount':
      modalContent = (
        <Suspension
          suspensionType={ENTITY_TYPE.ACCOUNT}
          accountId={selectedEmailAccountId}
          closeModal={handleCloseSuspensionUnsuspensionModal}
          email={props.queryText}
        />
      );
      break;
    case 'unsuspendDomain':
      suspensionType = ENTITY_TYPE.ORDER;
      modalContent = (
        <Unsuspension
          suspensionType={suspensionType}
          source={CustomerSuspensionSource.OrderDetails}
          accountId={getAccountId()}
          closeModal={handleCloseSuspensionUnsuspensionModal}
        />
      );
      break;
    case 'unsuspendOrder':
      suspensionType = ENTITY_TYPE.ORDER;
      modalContent = (
        <Unsuspension
          suspensionType={suspensionType}
          orderType={orderType}
          source={CustomerSuspensionSource.OrderDetails}
          closeModal={handleCloseSuspensionUnsuspensionModal}
        />
      );
      break;
    case 'unsuspendAccount':
      suspensionType = ENTITY_TYPE.ACCOUNT;
      suspensionListError =
        props.suspensionListInfo[suspensionType].suspensionListError;
      fetchSuspensionListSuccess =
        props.suspensionListInfo[suspensionType].fetchSuspensionListSuccess;
      suspensionList = props.suspensionListInfo[suspensionType].suspensionList;
      modalContent = (
        <Unsuspension
          suspensionType={suspensionType}
          source={CustomerSuspensionSource.OrderDetails}
          accountId={selectedEmailAccountId}
          closeModal={handleCloseSuspensionUnsuspensionModal}
        />
      );
      break;
    case 'suspendCustomer':
      modalContent = (
        <SuspendCustomerConfirmation
          closeModal={handleCloseSuspensionUnsuspensionModal}
          handleProceedClick={openCustomerSuspensionModal}
        />
      );
      break;
    case 'suspendCustomerConfirmation':
      modalContent = (
        <Suspension
          suspensionType={ENTITY_TYPE.CUSTOMER}
          closeModal={handleCloseSuspensionUnsuspensionModal}
        />
      );
      break;
    case 'unsuspendCustomer':
      modalContent = (
        <Unsuspension
          suspensionType={ENTITY_TYPE.CUSTOMER}
          source={CustomerSuspensionSource.OrderDetails}
          domainId={domainId}
          closeModal={handleCloseSuspensionUnsuspensionModal}
        />
      );
      break;
    case 'reactivateDomain':
      const orders = props.orderDetails.orders;
      const orderId = getLatestMailOrder(orders)?.id || null;

      if (!orderId) {
        toast.error(
          'Error opening reactivate modal. Missing order ID. Contact support.'
        );
        return;
      }

      modalContent = (
        <ReactivateDomainModal
          domainName={domainName}
          domainId={orderId}
          accountId={null}
          currentRegion={getCurrentRegion()}
          handleCloseOrderChange={handleCloseChangeOrderClick}
        />
      );
      break;
    case 'processRefund':
      modalContent = <ProcessRefund closeModal={handleCloseChangeOrderClick} />;
      break;
    case 'changeCustomerEmail':
      modalContent = (
        <ChangeCustomerEmailModal
          userId={bundle.customer.id}
          email={bundle.customer.email}
          closeModal={handleCloseChangeOrderClick}
        />
      );
      break;
    case 'modifyAccountLimit':
      modalContent = (
        <ModifyAccountLimitModal closeModal={handleCloseChangeOrderClick} />
      );
      break;
    case 'generateInvoice':
      modalContent = (
        <GenerateInvoice closeModal={handleCloseChangeOrderClick} />
      );
      break;
    default:
      break;
  }

  const reactivateDomainCheckError =
    props.domainReactivationCheckInfo.isDomainReactivationCheckFailure;
  if (suspensionListError) {
    let heading, subheading, description;
    if (suspensionType === ENTITY_TYPE.ORDER) {
      heading = props.intl.formatMessage({ id: 'unsuspend-domain' });
      subheading = domainName;
      description = props.intl.formatMessage(
        { id: 'could-not-fetch-suspension' },
        { entity: domainName }
      );
    } else {
      heading = props.intl.formatMessage({ id: 'unsuspend-account' });
      subheading = email;
      description = props.intl.formatMessage(
        { id: 'could-not-fetch-suspension' },
        { entity: email }
      );
    }
    modalContent = (
      <AlertModal
        heading={heading}
        subheading={subheading}
        description={description}
        buttonText={props.intl.formatMessage({ id: 'retry' })}
        buttonAction={retryGetAllSuspensions}
        closeAction={closeAlertModal}
      />
    );
  } else if (fetchSuspensionListSuccess && suspensionList.length === 0) {
    let heading, subheading, description;
    if (suspensionType === ENTITY_TYPE.ORDER) {
      heading = props.intl.formatMessage({ id: 'unsuspend-domain' });
      subheading = domainName;
      description = props.intl.formatMessage({
        id: 'no-active-suspension-domain',
      });
    } else {
      heading = props.intl.formatMessage({ id: 'unsuspend-account' });
      subheading = email;
      description = props.intl.formatMessage({
        id: 'no-active-suspension-account',
      });
    }
    modalContent = (
      <AlertModal
        heading={heading}
        subheading={subheading}
        description={description}
        buttonText={props.intl.formatMessage({ id: 'close' })}
        buttonAction={refreshOrderDetailsOnModalClose}
        closeAction={refreshOrderDetailsOnModalClose}
      />
    );
  } else if (props.customerSuspensionOrderInfo.fetchCustomerOrderListError) {
    let heading = props.intl.formatMessage({ id: 'suspend-customer' });
    let subheading = getCustomerEmail();
    let description = props.intl.formatMessage({
      id: 'order-list-customer-error',
    });
    modalContent = (
      <AlertModal
        heading={heading}
        subheading={subheading}
        description={description}
        buttonText={props.intl.formatMessage({ id: 'retry' })}
        buttonAction={() => handleSuspendCustomerClick('suspendCustomer')}
        closeAction={closeAlertModal}
      />
    );
  } else if (reactivateDomainCheckError) {
    let heading = props.intl.formatMessage({ id: 'reactivate-domain-header' });
    let subheading = domainName;
    let description = props.intl.formatMessage({
      id: 'reactivate-domain-check-failed',
    });
    modalContent = (
      <AlertModal
        heading={heading}
        subheading={subheading}
        description={description}
        buttonText={props.intl.formatMessage({ id: 'retry' })}
        buttonAction={() => handleReactivateDomainClicked()}
        closeAction={closeAlertModal}
      />
    );
  }

  let modalContentCss = 'delete-order-modal';
  if (props.isOrderChangePlanSuccess || props.isMailServiceChangeSuccess) {
    modalContentCss = 'configure-trial-success-modal';
  } else if (
    buttonClicked === 'suspendOrder' ||
    buttonClicked === 'suspendAccount'
  ) {
    modalContentCss = 'suspension-modal';
  } else if (fetchSuspensionListSuccess && suspensionList.length > 0) {
    modalContentCss = 'unsuspension-modal';
  } else if (
    suspensionListError ||
    (fetchSuspensionListSuccess && suspensionList.length === 0) ||
    reactivateDomainCheckError
  ) {
    modalContentCss = 'alert-modal';
  } else if (
    buttonClicked === 'suspendCustomer' &&
    !props.customerSuspensionOrderInfo.fetchCustomerOrderListError
  ) {
    modalContentCss = 'customer-order-for-suspension-modal';
  } else if (buttonClicked === 'suspendCustomerConfirmation') {
    modalContentCss = 'suspension-modal';
  }

  const fetchDomainSuspensionListInProgress =
    props.suspensionListInfo[ENTITY_TYPE.ORDER].fetchSuspensionListInProgress &&
    props.isOrderChangeInitiate;
  const fetchCustomerSuspensionListInProgress =
    props.suspensionListInfo[ENTITY_TYPE.CUSTOMER]
      .fetchSuspensionListInProgress && props.isOrderChangeInitiate;
  const fetchAccountSuspensionListInProgress =
    props.suspensionListInfo[ENTITY_TYPE.ACCOUNT]
      .fetchSuspensionListInProgress && props.isOrderChangeInitiate;
  const isCustomerOrderFetchInProgress =
    props.customerSuspensionOrderInfo.fetchCustomerOrderListInProgress;
  const getAllTransactionsInProgress = props.allTransactionsInfo.inProgress;
  const showSpinnerInDomainManageButton =
    props.isFetchAccountExceedingQuotaInProgress ||
    fetchDomainSuspensionListInProgress ||
    props.recoverEmailAccountState.fetchRecoverableAccountsInProgress ||
    fetchCustomerSuspensionListInProgress ||
    isCustomerOrderFetchInProgress ||
    getAllTransactionsInProgress ||
    props.domainReactivationCheckInfo.isDomainReactivationCheckInProgress;
  const showSpinnerInAccountManageButton = fetchAccountSuspensionListInProgress;
  const showSpinnerInReactivateButton =
    props.domainReactivationCheckInfo.isDomainReactivationCheckInProgress;
  const showModal =
    props.isOrderChangeInitiate &&
    !showSpinnerInDomainManageButton &&
    !showSpinnerInAccountManageButton &&
    !showSpinnerInReactivateButton;

  const domainActionHistoryButton =
    props.isFetchActionHistoryListInProgress &&
    props.actionHistoryEntityType === ENTITY_TYPE.ORDER ? (
      <div className="action-history-button-div">
        <FormattedMessage id="order-details-view-history-button-text" />
        <div className="spinner-manage" />
      </div>
    ) : (
      <FormattedMessage id="order-details-view-history-button-text" />
    );

  const getManageButtons = () => {
    const { intl } = props;
    const _isNeoPartner = isNeoPartner(bundle.partner.id);
    const _isMyPartner = isMyPartner(bundle.partner.id);
    const _isNeoOrMyPartner = _isNeoPartner || _isMyPartner;
    const mailOrder = allMailSuiteOrders[0];
    const siteOrder = allSiteOrders[0];
    const domainOrder = allDomainOrders[0];

    const buildOrderActions = (order) => {
      const actions = [];
      const orderStatus = order.status.toLowerCase();
      const isMailOrder = order.type === 'MailSuite';
      const isSiteOrder = order.type === 'Site';
      const isDomainOrder = order.type === 'NeoDomain';
      const planObj = isMailOrder
        ? getPlanForOrder(mailOrder)
        : isSiteOrder
          ? getPlanForOrder(siteOrder)
          : getPlanForOrder(domainOrder);
      if (isOrderStatusActive(orderStatus)) {
        if (order.isFreePlan) {
          const firstTrialPlan = getFirstTrialPlan(availablePlans);
          if (firstTrialPlan) {
            actions.push({
              title: intl.formatMessage(
                { id: 'move-to-trial' },
                { planName: firstTrialPlan.name }
              ),
              buttonName: ORDER_DETAIL_ACTION.MOVE_TO_TRIAL_PLAN,
              onClick: () =>
                handleChangePlanOrderClick(
                  ORDER_DETAIL_ACTION.MOVE_TO_TRIAL_PLAN,
                  order
                ),
            });
          }
        } else if (roleIsTitan) {
          // change expiry / extend plan
          actions.push({
            title: intl.formatMessage(
              { id: 'extend-current-plan' },
              { plan: toCapitalize(planObj.name) }
            ),
            buttonName: ORDER_DETAIL_ACTION.EXTEND_PLAN,
            onClick: () =>
              handleChangePlanOrderClick(
                ORDER_DETAIL_ACTION.EXTEND_PLAN,
                order
              ),
          });

          // change plan
          actions.push({
            title: intl.formatMessage({ id: 'change-plan' }),
            buttonName: ORDER_DETAIL_ACTION.CHANGE_PLAN,
            onClick: () =>
              handleChangePlanOrderClick(
                ORDER_DETAIL_ACTION.CHANGE_PLAN,
                order
              ),
          });
        }
        // TODO: test this condition
        if (!isOrderStatusSuspended(orderStatus)) {
          actions.push({
            title: intl.formatMessage({
              id: _isNeoOrMyPartner ? 'suspend-order' : 'suspend-domain',
            }),
            buttonName: 'suspendOrder',
            onClick: () => handleSuspendOrderClick(order),
          });
        }
        if (
          isMailOrder &&
          planObj.isPaid &&
          (roleIsTitan ||
            order.mailOrderDetails.accountCountPerStatus.active <
              order.mailOrderDetails.quota.accountsLimit)
        ) {
          actions.push({
            title: intl.formatMessage({ id: 'account-limit' }),
            buttonName: 'modifyAccountLimit',
            onClick: handleSetAccountLimitButtonClick,
          });
        }
      }
      if (isMailOrder) {
        let isCloudDomain = order.mailOrderDetails?.isFlockCloud;
        let serverType = order.mailOrderDetails?.serverType;
        if (shouldShowToggleButton) {
          let toggleMailServiceButtonText = '';
          if (isCloudDomain && serverType === 'cpanel') {
            toggleMailServiceButtonText = intl.formatMessage({
              id: 'move-to-cpanel',
            });
          } else if (!isCloudDomain) {
            toggleMailServiceButtonText = intl.formatMessage({
              id: 'move-to-flock-cloud',
            });
          }
          if (toggleMailServiceButtonText !== '') {
            actions.push({
              title: toggleMailServiceButtonText,
              buttonName: 'toggleMailService',
              onClick: handleToggleMailServiceClick,
            });
          }
        }
      }

      if (isOrderStatusSuspended(orderStatus)) {
        actions.push({
          title: intl.formatMessage({ id: 'unsuspend-order' }),
          buttonName: 'unsuspendDomain',
          onClick: () => handleUnsuspendOrderClick(order),
        });
      }
      if (!isDomainOrder) {
        if (isOrderStatusDeleted(orderStatus)) {
          actions.push({
            title: intl.formatMessage({ id: 'reactivate' }),
            buttonName: 'reactivateDomain',
            onClick: () => handleReactivateDomainClicked(order),
          });
        } else {
          if (isMailOrder) {
            actions.push({
              title: intl.formatMessage({ id: 'recover-deleted-data' }),
              buttonName: 'recoverDeletedOrderData',
              onClick: () => handleRecoverDataButtonClick(order),
            });
          }
          actions.push({
            title: intl.formatMessage({
              id: _isNeoOrMyPartner ? 'delete-order' : 'delete-domain',
            }),
            buttonName: 'deleteOrder',
            onClick: () => handleDeleteOrderClicked(order),
          });
        }
      }
      return actions;
    };

    const buildDomainOptionsActions = () => {
      const actions = [];
      const hasMailOrder = allMailSuiteOrders.length > 0;
      const hasSiteOrder = allSiteOrders.length > 0;
      const hasNonDeletedOrder =
        (hasMailOrder &&
          allMailSuiteOrders.some(
            (order) => !isOrderStatusDeleted(order.status.toLowerCase())
          )) ||
        (hasSiteOrder &&
          allSiteOrders.some(
            (order) => !isOrderStatusDeleted(order.status.toLowerCase())
          ));
      if (roleIsTitan && hasNonDeletedOrder) {
        actions.push({
          title: intl.formatMessage({ id: 'mark-domain-safe' }),
          buttonName: 'markDomainSafe',
          onClick: handleManageButtonClick,
        });
        actions.push({
          title: intl.formatMessage({ id: 'process-refund' }),
          buttonName: 'processRefund',
          onClick: handleProcessRefundClick,
        });
        actions.push({
          title: intl.formatMessage({ id: 'generate-invoice' }),
          buttonName: 'generateInvoice',
          onClick: handleManageButtonClick,
        });
      }
      actions.push({
        title: intl.formatMessage({ id: 'change-customer-email' }),
        buttonName: 'changeCustomerEmail',
        onClick: handleChangeCustomerEmailButtonClick,
      });
      return actions;
    };

    const buildCustomerOptionsActions = () => {
      let actions = [];
      if (props.isCustomerSuspended) {
        actions.push({
          title: intl.formatMessage({ id: 'unsuspend-customer' }),
          buttonName: 'unsuspendCustomer',
          onClick: handleUnsuspendCustomerClick,
        });
      } else {
        actions.push({
          title: intl.formatMessage({ id: 'suspend-customer' }),
          buttonName: 'suspendCustomer',
          onClick: handleSuspendCustomerClick,
        });
        if (roleIsTitan) {
          actions.push({
            title: intl.formatMessage({ id: 'mark-customer-safe' }),
            buttonName: 'markCustomerSafe',
            onClick: handleManageButtonClick,
          });
        }
      }
      return actions;
    };

    const mailOrderActions = mailOrder
      ? buildOrderActions(mailOrder, true)
      : [];
    const siteOrderActions = siteOrder
      ? buildOrderActions(siteOrder, false)
      : [];
    const domainOrderActions = domainOrder
      ? buildOrderActions(domainOrder)
      : [];
    const moreOptionsActions = [
      ...buildDomainOptionsActions(),
      ...buildCustomerOptionsActions(),
    ];
    const titanOrderActions = [
      {
        header: props.intl.formatMessage({ id: 'manage-domain' }),
        buttons: [...mailOrderActions, ...buildDomainOptionsActions()],
      },
      {
        header: props.intl.formatMessage({ id: 'manage-customer' }),
        buttons: buildCustomerOptionsActions(),
      },
    ];

    if (_isNeoOrMyPartner) {
      const manageMailOptions = {
        header: null,
        buttons: mailOrderActions,
      };
      const manageSiteOptions = {
        header: null,
        buttons: siteOrderActions,
      };
      const manageDomainOptions = {
        header: null,
        buttons: domainOrderActions,
      };
      const moreOptions = {
        header: null,
        buttons: moreOptionsActions,
      };
      return (
        <>
          {allMailSuiteOrders.length > 0 && (
            <ManageButton
              buttonLabelId={'manage-button-text-mail'}
              optionList={[manageMailOptions]}
              shouldShowSpinner={false}
              showDropdownOnTop={true}
            />
          )}
          {allSiteOrders.length > 0 && (
            <ManageButton
              optionList={[manageSiteOptions]}
              shouldShowSpinner={false}
              showDropdownOnTop={true}
              buttonLabelId={'manage-button-text-site'}
            />
          )}
          {allDomainOrders.length > 0 && domainOrderActions.length > 0 && (
            <ManageButton
              optionList={[manageDomainOptions]}
              shouldShowSpinner={false}
              showDropdownOnTop={true}
              buttonLabelId={'manage-button-text-domain'}
            />
          )}
          {bundle.status !== BUNDLE_STATUSES.DELETED &&
            bundle.status !== BUNDLE_STATUSES.PENDING && (
              <ManageButton
                optionList={[moreOptions]}
                shouldShowSpinner={false}
                showDropdownOnTop={true}
                buttonLabelId={'more-options'}
              />
            )}
        </>
      );
    } else {
      return (
        <ManageButton
          optionList={titanOrderActions}
          shouldShowSpinner={showSpinnerInDomainManageButton}
          showDropdownOnTop={true}
        />
      );
    }
  };

  return (
    <div ref={topDivRef} className="order-details-container">
      {props.isFetchingOrderDetails ? (
        <CircularProgress className="t-data-loader" size={26} />
      ) : null}
      <div className="order-details-box">
        {!props.isSingleOrder && (
          <NavigateBack
            className="navigate-back"
            onClick={() =>
              props.navigateBackBundleList(props.queryText, props.token)
            }
          />
        )}
        <div className="order-details-column">
          <div className="order-details-column-header">
            <div className="order-details-column-header-column">
              <div className="order-details-column-header-text">
                <FormattedMessage id="order-details-domain-information" />
              </div>
              <div className="rectangle"></div>
            </div>
          </div>
          <div className="order-info-box">
            {finalDomainObj}
            <div className="flex domain-info-buttons-container">
              {bundle.status === BUNDLE_STATUSES.DELETED && !_isNeoOrMyPartner
                ? getReactivateButton()
                : getManageButtons()}
              <button
                className="button button-secondary order-details-button"
                onClick={() => handleDomainHistoryClicked(domainId)}
              >
                {domainActionHistoryButton}
              </button>
              {showLoginToAdminPanelButton && (
                <CustomButton
                  onClick={() => handleAdminLoginPanelClick(bundle.id)}
                  type={'tertiary'}
                  isLoading={isAdminPanelUrlFetching}
                  className={'admin-panel-button'}
                >
                  <FormattedMessage id="order-details-admin-panel-button-text" />
                </CustomButton>
              )}
            </div>
          </div>
          <DnsDetails domainId={domainId} />
          {catchall && (
            <div className="order-info-single-item">
              <OrderDetailsItem
                itemName="order-details-catch-all"
                itemValue={catchall}
              />
            </div>
          )}
          {activeMailSuiteOrder?.mailOrderDetails?.accountCountPerStatus
            ?.suspended && (
            <div className="order-info-single-item">
              <OrderDetailsItem
                itemName="order-details-accounts-suspended"
                itemValue={
                  activeMailSuiteOrder.mailOrderDetails.accountCountPerStatus
                    .suspended
                }
              />
            </div>
          )}
        </div>
          {!mailSectionVisible && getLatestMailOrder(orders)?.id && (
            <DomainFeatureSettings
              mailOrderId={getLatestMailOrder(orders).id}
            />
          )}

        {/* EMAIL SECTION */}
        {mailSectionVisible && (
          <BundleDetailsEmailInfo
            mailOrderIds={mailOrderIds}
            renderStatusIndicator={renderStatusIndicator}
            handleSuspendUnsuspendEmailAccount={
              handleSuspendUnsuspendEmailAccount
            }
            refreshEmailInfo={refreshEmailInfo}
          />
        )}

        {showModal ? (
          <div className="modal">
            <div className={'modal-content ' + modalContentCss}>
              {props.isOrderDeleteSuccess && (
                <span
                  className="close-modal"
                  onClick={() => handleCloseChangeOrderClick()}
                >
                  <CloseGrey />
                </span>
              )}
              {modalContent}
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  partnerName: state.orders.orderDetails.partnerName,
  region: state.orders.orderDetails.region,
  partnerId: state.orders.partnerId,
  orderDetails: state.orders.orderDetails,
  dnsDetails: state.orders.orderDetails.dnsDetails,
  queryText: state.orders.queryText,
  isSingleOrder: state.orders.isSingleOrder,
  isOrderChangeInitiate: state.orders.isOrderChangeInitiate,
  token: state.auth.token,
  userRole: state.auth.userRole,
  userPartners: state.auth.userPartners,
  isFetchAccountExceedingQuotaInProgress:
    state.orders.isFetchAccountExceedingQuotaInProgress,
  noOfAccountsExceedingQuota: state.orders.noOfAccountsExceedingQuota,
  fetchAccountExceedingQuotaError: state.orders.fetchAccountExceedingQuotaError,
  isOrderDeleteSuccess: state.orders.isOrderDeleteSuccess,
  isOrderChangePlanSuccess: state.orders.isOrderChangePlanSuccess,
  isMailServiceChangeSuccess: state.orders.isMailServiceChangeSuccess,
  suspensionListInfo: state.orders.suspensionListInfo,
  isFetchActionHistoryListInProgress:
    state.orders.isFetchActionHistoryListInProgress,
  actionHistoryEntityType: state.orders.actionHistoryEntityType,
  customerSuspensionOrderInfo: state.orders.customerSuspensionOrderInfo,
  recoverEmailAccountState: state.orders.recoverEmailAccountState,
  domainReactivationCheckInfo: state.orders.domainReactivationCheckInfo,
  currentOrderSelected: state.orders.currentOrderSelected,
  allTransactionsInfo: state.orders.allTransactionsInfo,
  customerIdRegionDetails: state.orders.customerIdRegionDetails,
  isFetchingOrderDetails: state.orders.isFetchingOrderDetails,
  isCustomerSuspended:
    state.orders.entitySuspensionInfo?.[ENTITY_TYPE.CUSTOMER.toUpperCase()]
      ?.suspensions?.length > 0,
});

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators(
    {
      navigateBackBundleList,
      navigateBackOrderList,
      initiateChangeOrder,
      closeChangeOrder,
      fetchNoOfAccountsExceedingQuota,
      getAllSuspensions,
      fetchActionHistory,
      getCustomerSuspensions,
      fetchEmailAccountsToRecover,
      checkDomainReactivation,
      getEntitySuspensionDetails,
      fetchCustomerOrders,
    },
    dispatch
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(OrderDetails));
